import { useId } from "react";
import type { ReactNode } from "react";
import { Card } from "./card";
import { cn } from "../lib/cn";
import { CollapsibleToggle, useCollapsibleState } from "./collapsible";

type PageSectionProps = {
  title: string;
  description?: ReactNode;
  help?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  headingLevel?: 2 | 3 | 4;
  id?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  expandLabel?: string;
  collapseLabel?: string;
  toggleClassName?: string;
};

export function PageSection({
  title,
  description,
  help,
  actions,
  children,
  className,
  headingLevel = 3,
  id,
  collapsible = false,
  defaultExpanded,
  expandLabel,
  collapseLabel,
  toggleClassName,
}: PageSectionProps) {
  const titleId =
    id ??
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const HeadingTag = `h${headingLevel}` as const;
  const contentId = useId();
  const { expanded, toggleExpanded } = useCollapsibleState({ collapsible, defaultExpanded });
  const openLabel = expandLabel ?? `Expand ${title}`;
  const closeLabel = collapseLabel ?? `Collapse ${title}`;

  return (
    <section
      className={cn("cf-page-section", collapsible && expanded && "is-open", className)}
      aria-labelledby={titleId}
      data-collapsible={collapsible || undefined}
      data-expanded={collapsible ? expanded : undefined}
    >
      <Card>
        <div className="cf-grid cf-page-section-shell">
          <div className="cf-page-section-header">
            <div className="cf-grid cf-page-section-heading-group">
              <HeadingTag id={titleId}>{title}</HeadingTag>
              {description ? <p className="cf-page-section-description">{description}</p> : null}
              {help ? <p className="cf-help cf-page-section-help">{help}</p> : null}
            </div>
            {actions || collapsible ? (
              <div className="cf-page-section-actions">
                {actions}
                {collapsible ? (
                  <CollapsibleToggle
                    className={toggleClassName}
                    expanded={expanded}
                    expandLabel={openLabel}
                    collapseLabel={closeLabel}
                    onClick={toggleExpanded}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          {collapsible && !expanded ? null : (
            <div className="cf-page-section-content" id={contentId}>
              {children}
            </div>
          )}
        </div>
      </Card>
    </section>
  );
}
